import React, {useEffect, useMemo, useState} from "react";
import {
	Box,
	Button,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	useMediaQuery,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {useNavigate} from "react-router-dom";
import {OrgRequestRestControllerApiFactory, PresalesRowDto} from "arteco-api-client-ts";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import HourglassFullRoundedIcon from "@mui/icons-material/HourglassFullRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import SportsVolleyballRoundedIcon from "@mui/icons-material/SportsVolleyballRounded";
import EuroRoundedIcon from "@mui/icons-material/EuroRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import HandshakeRoundedIcon from "@mui/icons-material/HandshakeRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {useApiManager} from "../../../../components/utils/api";
import {dateToString} from "../../../../components/utils/dates";
import {
	btnSx,
	EmptyState,
	euros,
	FilterChips,
	ListCard,
	Page,
	PageHeader,
	Panel,
	relative,
	SearchField,
	SkeletonRows,
	SoftChip,
	StatGrid,
	StatTile,
	StatusDot,
	tableContainerSx,
	tableSx,
	Toolbar,
} from "../../../../components/kit";
import {ORG_REQUEST_STATUS} from "../labels";
import {amountOrDash, BALL, HoursMeter, IssueChips} from "./ui";

type Filter = "ALL" | "DUE" | "CAP" | "NO_ACTION" | "OURS";

const FILTERS: { value: Filter, label: string, test: (r: PresalesRowDto) => boolean }[] = [
	{value: "ALL", label: "Todas", test: () => true},
	{value: "DUE", label: "Vencidas", test: r => !!r.actionDue},
	{value: "CAP", label: "Tope alcanzado", test: r => !!r.capReached},
	{value: "NO_ACTION", label: "Sin próxima acción", test: r => !!r.noAction},
	{value: "OURS", label: "Pelota nuestra", test: r => r.ball === "OURS"},
];

const plain = (s?: string) => (s || "").toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");

const OPPORTUNITY = "/app/admin/marketing/org-request/";

const Opportunity = ({r}: { r: PresalesRowDto }) => (
	<Box sx={{minWidth: 0}}>
		<Stack direction="row" spacing={0.5} alignItems="center">
			<Typography variant="body2" fontWeight={600} sx={{minWidth: 0}}>{r.title}</Typography>
			{r.driveFolderUrl && (
				<Tooltip title="Carpeta de la propuesta en Drive">
					<IconButton size="small" href={r.driveFolderUrl} target="_blank" rel="noopener"
								aria-label={"Carpeta de Drive de " + r.title}
								onClick={e => e.stopPropagation()} sx={{color: "text.secondary"}}>
						<FolderOpenRoundedIcon sx={{fontSize: 16}}/>
					</IconButton>
				</Tooltip>
			)}
		</Stack>
		<Typography variant="caption" color="text.secondary" component="div">
			{r.organizationName ?? "Sin organización"}{r.contactName ? " · " + r.contactName : ""}
		</Typography>
	</Box>
);

const NextAction = ({r}: { r: PresalesRowDto }) => {
	if (!r.nextActionDate) {
		return <SoftChip tone="warning" label="Sin fecha"/>;
	}
	const overdue = (r.daysOverdue ?? 0) > 0;
	return (
		<Box>
			<Typography variant="body2" fontWeight={600}
						color={overdue ? "error.main" : r.actionDue ? "warning.dark" : undefined}>
				{relative(r.nextActionDate)}
			</Typography>
			<Typography variant="caption" color="text.secondary">{dateToString(r.nextActionDate)}</Typography>
		</Box>
	);
};

const Ball = ({r}: { r: PresalesRowDto }) => {
	const b = (r.ball && BALL[r.ball]) || BALL.NOBODY;
	return (
		<Box>
			<StatusDot tone={b.tone} label={b.label}/>
			<Typography variant="caption" color="text.secondary" component="div" sx={{pl: 2}}>{r.ballReason}</Typography>
		</Box>
	);
};

const Status = ({r}: { r: PresalesRowDto }) => {
	const s = r.status ? ORG_REQUEST_STATUS[r.status] : undefined;
	return s ? <SoftChip label={s.label} tone={s.tone}/> : null;
};

/** En móvil, una tarjeta por oportunidad. */
const PresalesCard = ({r, onOpen}: { r: PresalesRowDto, onOpen: () => void }) => {
	const Item = ({label, children}: { label: string, children: React.ReactNode }) => (
		<Box sx={{minWidth: 0}}>
			<Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
			{children}
		</Box>
	);
	return (
		<ListCard onClick={onOpen}>
			<Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
				<Opportunity r={r}/>
				<Status r={r}/>
			</Stack>
			<Box sx={{display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 1.5, mt: 2}}>
				<Item label="Próxima acción"><NextAction r={r}/></Item>
				<Item label="Pelota"><Ball r={r}/></Item>
				<Item label="Horas de preventa"><HoursMeter spent={r.hoursSpent} cap={r.hoursCap} own={r.hoursCapOwn} width="100%"/></Item>
				<Item label="Importe">
					<Typography variant="body2" sx={{fontVariantNumeric: "tabular-nums"}}>{amountOrDash(r.amount)}</Typography>
				</Item>
				<Box sx={{gridColumn: "1 / -1"}}><Item label="Issues"><IssueChips ids={r.redmineIssueIds} issues={r.issues}/></Item></Box>
			</Box>
		</ListCard>
	);
};

/**
 * Preventa (#14762, wf-preventa.md): cada oportunidad viva con su trabajo, sus horas frente al tope
 * y de quién es la pelota. Vencidas primero, después las que han llegado al tope y el resto por fecha.
 */
export const PresalesPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(OrgRequestRestControllerApiFactory);
	const navigate = useNavigate();
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));
	const [rows, setRows] = useState<PresalesRowDto[] | undefined>(undefined);
	const [filter, setFilter] = useState<Filter>("ALL");
	const [term, setTerm] = useState("");
	const [refreshing, setRefreshing] = useState(false);

	useEffect(() => {
		apiManager.execute(ctrl.presalesOrgRequest()).then(setRows).catch(() => setRows([]));
	}, []);

	const refresh = () => {
		setRefreshing(true);
		apiManager.execute(ctrl.refreshPresalesOrgRequest(), "Horas releídas de Redmine")
			.then(setRows)
			.finally(() => setRefreshing(false));
	};

	const all = rows ?? [];
	const counts = useMemo(() => Object.fromEntries(FILTERS.map(f => [f.value, all.filter(f.test).length])), [rows]);
	const test = FILTERS.find(f => f.value === filter)!.test;
	const visible = all.filter(r => test(r) && (!term || plain(r.title + " " + r.organizationName + " " + r.contactName).includes(plain(term))));
	const amount = all.reduce((sum, r) => sum + (r.amount ?? 0), 0);
	const readAt = all.map(r => r.presalesReadAt).filter(Boolean).sort().pop();
	const open = (r: PresalesRowDto) => navigate(OPPORTUNITY + r.id);

	return (
		<Page>
			<PageHeader
				title="Preventa"
				subtitle={rows === undefined ? "Cargando…"
					: `${all.length} ${all.length === 1 ? "oportunidad viva" : "oportunidades vivas"}`
					+ (readAt ? ` · horas leídas de Redmine ${relative(readAt).replace("hoy", "hoy a las " + readAt.slice(11, 16))}` : "")}
				actions={
					<Button variant="outlined" startIcon={<RefreshRoundedIcon/>} onClick={refresh} disabled={refreshing} sx={btnSx}>
						{refreshing ? "Leyendo Redmine…" : "Actualizar horas"}
					</Button>
				}/>

			<StatGrid>
				<StatTile icon={<EventBusyRoundedIcon fontSize="small"/>} tone={counts.DUE ? "error" : "neutral"}
						  value={counts.DUE} loading={!rows} label="Próxima acción vencida o hoy" onClick={() => setFilter("DUE")}/>
				<StatTile icon={<HourglassFullRoundedIcon fontSize="small"/>} tone={counts.CAP ? "error" : "neutral"}
						  value={counts.CAP} loading={!rows} label="Tope de horas alcanzado" onClick={() => setFilter("CAP")}/>
				<StatTile icon={<EventNoteRoundedIcon fontSize="small"/>} tone={counts.NO_ACTION ? "warning" : "neutral"}
						  value={counts.NO_ACTION} loading={!rows} label="Sin próxima acción" onClick={() => setFilter("NO_ACTION")}/>
				<StatTile icon={<SportsVolleyballRoundedIcon fontSize="small"/>} tone="info"
						  value={counts.OURS} loading={!rows} label="Pelota nuestra" onClick={() => setFilter("OURS")}/>
				<StatTile icon={<EuroRoundedIcon fontSize="small"/>} tone="neutral"
						  value={euros(amount, 0)} loading={!rows} label="Importe esperado"/>
			</StatGrid>

			<Panel>
				<Toolbar
					left={<FilterChips options={FILTERS.map(f => ({value: f.value, label: f.label, count: counts[f.value]}))}
									   value={filter} onChange={setFilter}/>}
					right={<SearchField value={term} onChange={setTerm} placeholder="Buscar oportunidad u organización"/>}/>

				{rows === undefined ? <SkeletonRows/> : visible.length === 0 ? (
					<EmptyState icon={<HandshakeRoundedIcon/>}
								title={all.length === 0 ? "No hay oportunidades vivas" : "Ninguna coincide con el filtro"}
								description={all.length === 0
									? "Cuando un lead pida un análisis, una propuesta o una demo, dale de alta su oportunidad en Oportunidades y enlaza ahí sus issues."
									: "Prueba con «Todas» o quita la búsqueda."}/>
				) : compact ? (
					<Stack spacing={1.5}>{visible.map(r => <PresalesCard key={r.id} r={r} onOpen={() => open(r)}/>)}</Stack>
				) : (
					<TableContainer sx={tableContainerSx}>
						<Table size="small" sx={{...tableSx, tableLayout: "auto"}}>
							<TableHead>
								<TableRow>
									<TableCell>Oportunidad</TableCell>
									<TableCell>Pelota</TableCell>
									<TableCell>Próxima acción</TableCell>
									<TableCell>Issues</TableCell>
									<TableCell>Horas de preventa</TableCell>
									<TableCell align="right">Importe</TableCell>
									<TableCell/>
								</TableRow>
							</TableHead>
							<TableBody>
								{visible.map(r => (
									<TableRow key={r.id} hover onClick={() => open(r)} sx={{cursor: "pointer"}}>
										<TableCell sx={{minWidth: 280, maxWidth: 380}}>
											<Opportunity r={r}/>
											<Box sx={{mt: 0.5}}><Status r={r}/></Box>
										</TableCell>
										<TableCell sx={{minWidth: 150}}><Ball r={r}/></TableCell>
										<TableCell sx={{whiteSpace: "nowrap"}}><NextAction r={r}/></TableCell>
										<TableCell sx={{maxWidth: 200}}><IssueChips ids={r.redmineIssueIds} issues={r.issues}/></TableCell>
										<TableCell><HoursMeter spent={r.hoursSpent} cap={r.hoursCap} own={r.hoursCapOwn}/></TableCell>
										<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"}}>{amountOrDash(r.amount)}</TableCell>
										<TableCell align="right" sx={{width: 32, color: "text.disabled"}}><ChevronRightRoundedIcon fontSize="small"/></TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}

				<Stack direction="row" spacing={1} alignItems="flex-start" sx={{mt: 2, color: "text.secondary"}}>
					<InfoOutlinedIcon sx={{fontSize: 16, mt: "2px"}}/>
					<Typography variant="caption">
						El tope de horas es el de Configuración › Parámetros (presales.hours-cap) salvo que la ficha fije uno propio; al llegar a él se para y decide Ramón
						(wf-preventa).
						La pelota es nuestra si hay una issue abierta o la próxima acción ha llegado, y suya mientras esperamos
						a la fecha. Las horas se releen de Redmine cada hora; el aviso por correo sale los laborables a las 8:25.
					</Typography>
				</Stack>
			</Panel>
		</Page>
	);
};
